import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailLink,
  signOut as fbSignOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isOwner: boolean | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  completeEmailLinkSignIn: (email: string) => Promise<void>;
  refreshOwnerStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState<boolean | null>(null);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (next) => {
      setUser(next);
      if (next) {
        try {
          const snap = await getDoc(doc(db, 'clients', next.uid));
          setIsOwner(snap.exists());
        } catch {
          setIsOwner(false);
        }
      } else {
        setIsOwner(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isOwner,
      signInWithGoogle: async () => {
        await signInWithPopup(auth, googleProvider);
      },
      signOut: async () => {
        await fbSignOut(auth);
      },
      getIdToken: async (forceRefresh = false) => {
        if (!auth.currentUser) return null;
        return auth.currentUser.getIdToken(forceRefresh);
      },
      completeEmailLinkSignIn: async (email: string) => {
        await signInWithEmailLink(auth, email, window.location.href);
      },
      refreshOwnerStatus: async () => {
        const current = auth.currentUser;
        if (!current) return;
        try {
          const snap = await getDoc(doc(db, 'clients', current.uid));
          setIsOwner(snap.exists());
        } catch {
          setIsOwner(false);
        }
      },
    }),
    [user, loading, isOwner],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
